import { FC, MouseEvent, useCallback, useEffect, useMemo } from 'react';

import { push } from 'connected-react-router';
import {
    CampaignCreationWizardStep,
    createCampaignCreationWizardBreadcrumbClickHandler,
    isCampaignCreationWizardHistoryState,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CREATE_MENU_PAGE_ROUTE } from 'utils/constants/app';
import {
    CAMPAIGN_AVAILABLE_STEPS,
    CampaignProcess,
    CampaignProductType,
    CampaignSteps,
} from 'utils/constants/campaign';

import { Link, Text } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import { useCampaignCreationWizardBackAlert } from 'hooks/useCampaignCreationWizardBackAlert';
import { useCampaignCreationWizardStepNavigation } from 'hooks/useCampaignCreationWizardStepNavigation';
import { useDashboardRoute } from 'hooks/useDashboardRoute';

import styles from 'pages/CpcFlexibleBuilderPage/CpcFlexibleBuilderPage.module.less';

import { PageContentHeader } from 'components/common/PageContentHeader/PageContentHeader';
import { VacancyLink } from 'components/common/VacancyLink/VacancyLink';
import { CpcFlexibleCreate } from 'components/CpcFlexibleCreate/CpcFlexibleCreate';
import {
    CPC_FLEXIBLE_TITLE_TEXTS_REDESIGN,
    useCpcFlexibleCreateData,
} from 'components/CpcFlexibleCreate/useCpcFlexibleCreateData';
import { Header } from 'components/Header/Header';

import { CampaignTarget, WizardCpcMode } from 'store/models/campaignCreationWizard';
import { selectCampaignProcess, setCampaignFormStep } from 'store/models/createCampaign';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    breadcrumbs: {
        dashboard: 'AdCreatePage.breadcrumbs.dashboard',
        main: 'CampaignCreationWizard.breadcrumbs.main',
        [CampaignProcess.Create]: 'AdCreatePage.breadcrumbs.new',
        [CampaignProcess.Edit]: 'AdCreatePage.breadcrumbs.edit',
        [CampaignProcess.Copy]: 'AdCreatePage.breadcrumbs.copy',
        settings: 'CampaignCreationWizardFlow.breadcrumbs.settings',
        creation: 'CampaignCreationWizard.breadcrumbs.creation',
        promotionType: 'CampaignCreationWizard.breadcrumbs.promotionType',
    },
};

const CPC_FLEXIBLE_WIZARD_STEPS = [CampaignSteps.First, CampaignSteps.Second, CampaignSteps.Third];

export const CpcFlexibleBuilderPage: FC = () => {
    const step = useSelector((state) => state.router.location.query?.step) as CampaignSteps;
    const isWizardFlow = useSelector((state) => isCampaignCreationWizardHistoryState(state.router.location.state));
    const currentStep = String(useSelector((state) => state.createCampaign.currentStep)) as CampaignSteps;
    const campaignProcess = useSelector(selectCampaignProcess);
    const campaignId = useSelector((state) => state.createCampaign.campaignId);
    const { bannerVacancyId, bannerLink, vacancyName } = useSelector((state) => state.createBanner);

    const { titleText } = useCpcFlexibleCreateData();
    const dispatch = useDispatch();
    const trls = useTrls();
    const dashboardRoute = useDashboardRoute();
    const isWizardFlowEnabled = campaignProcess === CampaignProcess.Create && isWizardFlow;
    const shouldShowPromotionTypeBreadcrumb = Boolean(bannerVacancyId);
    const { backAlert, openBackAlert } = useCampaignCreationWizardBackAlert();
    const goToWizardStep = useCampaignCreationWizardStepNavigation();

    const openWizardBackAlert = useCallback(
        (activeStep: CampaignCreationWizardStep) => {
            openBackAlert(() =>
                goToWizardStep(activeStep, {
                    vacancyId: bannerVacancyId ? Number(bannerVacancyId) : null,
                    vacancyName: vacancyName ?? undefined,
                    productType: CampaignProductType.Cpc,
                    cpcMode: WizardCpcMode.Flexible,
                    ...(bannerLink &&
                        !bannerVacancyId && {
                            websiteUrl: bannerLink,
                            campaignTarget: CampaignTarget.Website,
                        }),
                })
            );
        },
        [openBackAlert, goToWizardStep, bannerVacancyId, vacancyName, bannerLink]
    );

    let additionalContent = null;
    if (bannerLink && bannerVacancyId) {
        additionalContent = (
            <VacancyLink dataQa="header-vacancy-link" vacancyId={bannerVacancyId} vacancyName={vacancyName} />
        );
    } else if (bannerLink) {
        additionalContent = (
            <Link href={bannerLink} target="_blank" data-qa="header-banner-link" overflowWrap="anywhere">
                <Text maxLines={1}>{bannerLink}</Text>
            </Link>
        );
    }

    const breadcrumbs = useMemo(() => {
        if (isWizardFlowEnabled) {
            return [
                {
                    to: dashboardRoute,
                    children: trls(TrlKeys.breadcrumbs.main),
                    onClick: (e: MouseEvent) => {
                        e.preventDefault();
                        openBackAlert(() => dispatch(push(dashboardRoute)));
                    },
                },
                {
                    to: CREATE_MENU_PAGE_ROUTE,
                    children: trls(TrlKeys.breadcrumbs.creation),
                    onClick: createCampaignCreationWizardBreadcrumbClickHandler(
                        openWizardBackAlert,
                        CampaignCreationWizardStep.PromotionObject
                    ),
                },
                ...(shouldShowPromotionTypeBreadcrumb
                    ? [
                          {
                              to: CREATE_MENU_PAGE_ROUTE,
                              children: trls(TrlKeys.breadcrumbs.promotionType),
                              onClick: createCampaignCreationWizardBreadcrumbClickHandler(
                                  openWizardBackAlert,
                                  CampaignCreationWizardStep.PromotionType
                              ),
                          },
                      ]
                    : []),
                ...CPC_FLEXIBLE_WIZARD_STEPS.filter((wizardStep) => Number(wizardStep) <= Number(currentStep)).map(
                    (wizardStep) => ({
                        to: '#',
                        children: trls(CPC_FLEXIBLE_TITLE_TEXTS_REDESIGN[CampaignProcess.Create][wizardStep]),
                        ...(wizardStep !== currentStep && {
                            onClick: (event: MouseEvent) => {
                                event.preventDefault();
                                dispatch(setCampaignFormStep(Number(wizardStep)));
                            },
                        }),
                    })
                ),
            ];
        }

        return [
            {
                to: dashboardRoute,
                children: trls(TrlKeys.breadcrumbs.dashboard),
            },
            { to: '#', children: trls(TrlKeys.breadcrumbs[campaignProcess]) },
        ];
    }, [
        campaignProcess,
        currentStep,
        dashboardRoute,
        dispatch,
        isWizardFlowEnabled,
        shouldShowPromotionTypeBreadcrumb,
        openBackAlert,
        openWizardBackAlert,
        trls,
    ]);

    useEffect(() => {
        if (campaignId && step && CAMPAIGN_AVAILABLE_STEPS.includes(step)) {
            dispatch(setCampaignFormStep(parseInt(step, 10)));
        }
    }, [campaignId, dispatch, step]);

    return (
        <div className={styles.wrapper}>
            <Header />
            <PageContentHeader
                breadcrumbsConfig={breadcrumbs}
                title={trls(titleText)}
                additionalContent={additionalContent}
                vSpacingBottom={24}
            />
            <CpcFlexibleCreate />
            {isWizardFlowEnabled && backAlert}
        </div>
    );
};
